import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';

import { NAVY, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from '../../../styles/tokens';
import { PENDING_CHAT_BG, PENDING_CHAT_COUNT_COLOR } from './LeadCard';
import type { KanbanColumnConfig } from './VirtualizedKanbanColumn';

interface CollapsedColumnProps {
  status: string;
  config: KanbanColumnConfig;
  totalCount: number;
  pendingChatCount?: number;
  statusCountLoading: boolean;
  onToggleCollapse: (status: string) => void;
}

/**
 * Collapsed column component for Kanban board
 * Displays a vertical title, lead count, and makes the entire column clickable to expand
 *
 * @param {Object} props - Component props
 * @param {string} props.status - Column status identifier
 * @param {Object} props.config - Column configuration (label, icon, color, tooltip)
 * @param {number} props.totalCount - Total number of leads in this column
 * @param {boolean} props.statusCountLoading - Whether the status count is loading
 * @param {Function} props.onToggleCollapse - Handler for expanding the column
 * @returns {JSX.Element} Collapsed column component
 */
const CollapsedColumn: FC<CollapsedColumnProps> = ({
  status,
  config,
  totalCount,
  pendingChatCount,
  statusCountLoading,
  onToggleCollapse,
}) => {
  return (
    <Box
      sx={{
        flex: '0 0 48px',
        minWidth: '48px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: SURFACE_CONTAINER_LOW,
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'all 0.3s ease',
      }}
    >
      <Tooltip title={config.tooltip} placement="right" arrow>
        <Box
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse(status);
          }}
          sx={{
            borderTop: `3px solid ${config.color}`,
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 2,
            pt: 2,
            pb: 2,
            height: '100%',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: SURFACE_CONTAINER_LOW,
            },
          }}
        >
          <Chip
            label={
              statusCountLoading ? (
                <CircularProgress size={12} sx={{ color: config.textColor }} />
              ) : (
                String(totalCount).padStart(2, '0')
              )
            }
            size="small"
            sx={{
              backgroundColor: config.color,
              color: config.textColor,
              fontWeight: 700,
              fontFamily: 'Jost',
              minWidth: 32,
              height: 24,
            }}
          />
          {!statusCountLoading && pendingChatCount !== undefined && (
            <Chip
              label={String(pendingChatCount).padStart(2, '0')}
              size="small"
              sx={{
                backgroundColor: PENDING_CHAT_BG,
                color: PENDING_CHAT_COUNT_COLOR,
                fontWeight: 700,
                fontFamily: 'Jost',
                minWidth: 32,
                height: 24,
              }}
            />
          )}
          <Typography
            sx={{
              transform: 'rotate(180deg)',
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              fontSize: '0.8rem',
              fontWeight: 700,
              fontFamily: 'Jost',
              color: NAVY,
              letterSpacing: '0.02em',
            }}
          >
            {config.label}
          </Typography>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default CollapsedColumn;
